/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { Button, ButtonVariety, Modal, ModalSize, Text } from '@sonarsource/echoes-react';
import { ReactNode, useCallback, useContext, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Image } from '~adapters/components/common/Image';
import { useRouter } from '~shared/components/hoc/withRouter';
import { isLoggedIn } from '~shared/helpers/users';
import DocumentationLink from '~sq-server-commons/components/common/DocumentationLink';
import { CurrentUserContext } from '~sq-server-commons/context/current-user/CurrentUserContext';
import { DocLink } from '~sq-server-commons/helpers/doc-links';
import { getGlobalSettingsUrl } from '~sq-server-commons/helpers/urls';
import { useDismissNoticeMutation } from '~sq-server-commons/queries/users';
import { Permissions } from '~sq-server-commons/types/permissions';
import { NoticeType } from '~sq-server-commons/types/users';
import { useSandboxSettingsQuery } from '../queries';

export default function SandboxIssuesIntro() {
  const { currentUser } = useContext(CurrentUserContext);
  const router = useRouter();
  const intl = useIntl();
  const { mutate: dismissNotice } = useDismissNoticeMutation();

  const dismissedTour = currentUser.dismissedNotices?.[NoticeType.SANDBOXED_ISSUES_INTRO];
  const isAdmin = currentUser.permissions?.global.includes(Permissions.Admin);
  const { data: sandboxSettingEnabled } = useSandboxSettingsQuery({
    select: (data) => data.enabled,
    enabled: Boolean(isAdmin),
  });
  const showIntro =
    isLoggedIn(currentUser) && isAdmin && !dismissedTour && sandboxSettingEnabled === false;

  const dismissIntro = useCallback(() => {
    dismissNotice(NoticeType.SANDBOXED_ISSUES_INTRO);
  }, [dismissNotice]);

  const handleTurnOn = () => {
    dismissNotice(NoticeType.SANDBOXED_ISSUES_INTRO, {
      onSuccess: () => {
        router.push(
          getGlobalSettingsUrl('general', { sandbox: 'enable' }, '#sonar.issues.sandbox.enabled'),
        );
      },
    });
  };

  // Mark the intro as dismissed for logged in users: non-admins or if the sandbox is already enabled
  useEffect(() => {
    if (isLoggedIn(currentUser) && !dismissedTour && (!isAdmin || sandboxSettingEnabled === true)) {
      dismissIntro();
    }
  }, [currentUser, dismissedTour, dismissIntro, isAdmin, sandboxSettingEnabled]);

  if (!showIntro) {
    return null;
  }

  return (
    <Modal
      content={
        <>
          <Image
            alt={intl.formatMessage({ id: `sandboxed_issues_intro.img_alt` })}
            className="sw-w-full sw-mb-4"
            src="/images/sandboxed-issues-intro.png"
          />
          <Text as="p" className="sw-min-w-full">
            <FormattedMessage id="sandboxed_issues_intro.description_1" />
          </Text>
          <Text as="p" className="sw-my-4 sw-min-w-full">
            <FormattedMessage
              id="sandboxed_issues_intro.description_2"
              values={{
                b: HighligtetedText,
              }}
            />
          </Text>
        </>
      }
      footerLink={
        <DocumentationLink enableOpenInNewTab standalone to={DocLink.InstanceAdminSandboxIssues}>
          <FormattedMessage id="sandboxed_issues_intro.footer_link" />
        </DocumentationLink>
      }
      isOpen
      onOpenChange={dismissIntro}
      primaryButton={
        <Button onClick={handleTurnOn} variety={ButtonVariety.Primary}>
          <FormattedMessage id="sandboxed_issues_intro.turn_on" />
        </Button>
      }
      secondaryButton={
        <Button onClick={dismissIntro} variety={ButtonVariety.Default}>
          <FormattedMessage id="sandboxed_issues_intro.no_thanks" />
        </Button>
      }
      size={ModalSize.Wide}
      title={<FormattedMessage id="sandboxed_issues_intro.title" />}
    />
  );
}

function HighligtetedText(text: ReactNode) {
  return <Text isHighlighted>{text}</Text>;
}
